import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import { Box, Button, Typography, Chip } from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Add } from "@mui/icons-material";
import AddModalView from "../AddModalView/AddModalView";
import { VISIBLE_STATE_CASES } from "../../Models/StateConstants.ts";
import { GuestsStoreContext } from "../../Store/GuestsStore.ts";
import GuestsAdd from "./GuestsAdd.tsx";
import { getGuests } from "../../Requests/GuestService.ts";
import { GuestModel } from "../../Models/Guest.ts";
import { useRestoreScrollPosition } from "../../Hooks/useScrollRestoration";
import { useTableState } from "../../Hooks/useTableState";
import { useFilteredBySearch } from "../../Hooks/useFilteredBySearch";
import TableToolbarWithSearch from "../TableToolbar/TableToolbarWithSearch.jsx";
import ColumnsIcon from "../TableToolbar/ColumnsIcon.jsx";

const SEARCH_FIELD_OPTIONS = [
  { value: 'fullName', label: 'Name' },
  { value: 'occupation', label: 'Occupation' }
]

const COLUMNS = [
  {
    accessorFn: (originalRow) => originalRow.firstName + " " + originalRow.lastName,
    header: 'Guest',
    Cell: ({ renderedCellValue, row }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <img
          alt="avatar"
          height={32}
          width={32}
          src={row.original.avatar}
          loading="lazy"
          style={{ borderRadius: '50%' }}
        />
        <span>{renderedCellValue}</span>
      </Box>
    )
  },
  {
    accessorKey: 'occupation',
    header: 'Occupation',
    enableGlobalFilter: false,
  },
  {
    accessorFn: (originalRow) => new Date(originalRow.createdAt),
    accessorKey: 'createdAt',
    header: 'Date Created',
    filterVariant: 'date-range',
    enableGlobalFilter: false,
    Cell: ({ cell }) => dayjs(cell.getValue()).format('DD MMM YYYY'),
  },
  {
    accessorKey: 'hiddenState',
    header: 'Visibility',
    enableGlobalFilter: false,
    filterVariant: 'select',
    filterSelectOptions: VISIBLE_STATE_CASES.map((value) => value.label),
    Cell: ({ cell }) => (
      <Chip label={cell.getValue()} />
    )
  }
]


export default function Guests() {

  const store = useContext(GuestsStoreContext)

  const [columnVisibility, setColumnVisibility] = useState(store.columnVisibility)
  useEffect(() => {
    store.columnVisibility = columnVisibility
  }, [columnVisibility, store])

  const [columnFilters, setColumnFilters] = useState(store.columnFilters)
  useEffect(() => {
    store.columnFilters = columnFilters
  }, [columnFilters, store])
  const [globalFilter, setGlobalFilter] = useTableState(store, 'globalFilter', undefined)
  const [searchField, setSearchField] = useTableState(store, 'searchField', SEARCH_FIELD_OPTIONS[0].value)

  const [pagination, setPagination] = useState(store.pagination)
  useEffect(() => {
    store.pagination = pagination
  }, [pagination, store])

  const navigate = useNavigate()
  const tableContainerRef = useRef(null)

  // Use store for state preservation
  const [open, setOpen] = useState<boolean>(store.open)
  useEffect(() => {
    store.open = open
  }, [open, store])

  const [isLoading, setIsloading] = useState<boolean>(store.isLoading)
  useEffect(() => {
    store.isLoading = isLoading
  }, [isLoading, store])

  const [isRefetching, setIsRefetching] = useState<boolean>(store.isRefetching)
  useEffect(() => {
    store.isRefetching = isRefetching
  }, [isRefetching, store])

  const [tableData, setTableData] = useState<GuestModel[]>(store.tableData)
  useEffect(() => {
    store.tableData = tableData
  }, [tableData, store])

  const fetchData = async () => {
    const result = await getGuests()
    setIsloading(false)
    setIsRefetching(false)
    setTableData(result)
  }

  useEffect(() => {
    // Restore state from store if data exists
    if (store.tableData.length > 0 && !store.needsRefresh) {
      setTableData(store.tableData)
      setIsloading(false)
      setIsRefetching(false)
    } else {
      // Fetch data if not loaded or refresh is needed
      if (store.needsRefresh) {
        store.needsRefresh = false
      }
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Restore scroll position after table is rendered and data is loaded
  useRestoreScrollPosition(tableContainerRef, store, tableData, isLoading)

  const columns = useMemo<MRT_ColumnDef<GuestModel>[]>(() => COLUMNS, [])

  const getRowSearchValue = useMemo(
    () => (row: GuestModel, field: string) =>
      field === 'fullName' ? [row.firstName, row.lastName].filter(Boolean).join(' ') : (row as Record<string, unknown>)[field],
    []
  )
  const data = useFilteredBySearch(tableData, globalFilter, searchField, getRowSearchValue)

  const table = useMaterialReactTable({
    columns,
    data,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    state: {
      isLoading: isLoading,
      showProgressBars: isRefetching,
      columnFilters: columnFilters,
      columnVisibility: columnVisibility,
      globalFilter: globalFilter,
      showGlobalFilter: false,
      pagination: pagination
    },
    icons: {
      ViewColumnIcon: ColumnsIcon,
    },
    muiFilterDatePickerProps: {
      format: 'DD/MM/YYYY',
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        // Save scroll position before navigation
        if (tableContainerRef.current) {
          store.scrollTop = tableContainerRef.current.scrollTop
        }
        navigate(`/guest/${row.original.objectId}`)
      },
      sx: {
        cursor: 'pointer'
      }
    }),
    renderTopToolbar: ({ table }) => (
      <TableToolbarWithSearch
        table={table}
        exportFileName="guests"
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        searchField={searchField}
        onSearchFieldChange={setSearchField}
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        selectMinWidth={140}
        action={
          <Button
            variant="contained"
            size="large"
            onClick={() => setOpen(true)}
          >
            <Add />
            Add Guest
          </Button>
        }
      />
    ),
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid",
        color: "grey.300"
      }
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: {
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto'
      },
      onScroll: (e) => {
        // Save scroll position while scrolling
        if (e.target instanceof HTMLElement) {
          store.scrollTop = e.target.scrollTop
        }
      }
    }
  })

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        paddingTop={2}
        paddingBottom={1}
      >
        <Typography
          variant="h4"
        >
          Guests
        </Typography>
      </Box>

      <MaterialReactTable
        table={table}
      />

      <AddModalView
        isOpen={open}
        setIsOpen={setOpen}
        title={"Add Guest"}
        view={
          <GuestsAdd publish={() => {
            setOpen(false)
            setIsRefetching(true)
            fetchData()
          }} />}
      />
    </>
  )
}
